import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Line as LineChart } from 'react-chartjs-2'
import { fetchRatingsHistory, fetchEarliestRecord, setWtnChartRange } from 'actions/ratings-actions'
import styles from './history-line-chart.module.scss'
import i18n from 'constants/strings'
import { capitalize, checkDatasetEmpty } from '../../../utils'
import { AppState } from 'redux/reducers'
import { getHistoryLineChartOptions } from './HistoryLineChartOptions'
import {
  createDataset,
  formatData,
  getDataKeyFromLabel,
  getYAxisBounds,
} from './HistoryLineChartUtilities'
import { GameType } from '../ResultsDoughnutChart/ResultsDoughnutChartTypes'
import useWindowSize from 'hooks/use-windowSize'
import { matchTypes, TABLET_BREAKPOINT } from 'constants/global'
import FilterDropdown from 'components/generic/FilterDropdown'
import ChartMobileFilter from '../../generic/MobileFilterSelector/MobileFilterSelector'
import NoDataContainer from 'components/generic/NoDataContainer'
import { LineChartLabel, MatchUpTypeEnum } from 'types/global'
import cx from 'classnames'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'
import { DateRange } from 'components/generic/DatePicker/types'
import DateRangePickerWTN from 'components/generic/DatePicker/DateRangePickerWTN'
const keyCode = ['#2A3B6B', '#E53E51', '#3AC6D1', '#1E4B3C']
const defaultLabels = [
  { type: MatchUpTypeEnum.SINGLES, name: i18n.t('strCharts.singles'), keyCol: 0 },
  { type: MatchUpTypeEnum.DOUBLES, name: i18n.t('strCharts.doubles'), keyCol: 1 },
]

function chartData({
  data,
  chartType,
  dates,
  labels,
  isDesktop,
}: {
  data: {
    [k: string]: (string | null)[]
  }
  chartType: GameType
  dates: string[]
  labels: LineChartLabel[]
  isDesktop: boolean
}) {
  const datasets = Object.entries(data)
    .map(([key, value]) => {
      const { keyCol, name } = labels.find(label => getDataKeyFromLabel(label) === key) ?? {}

      if (key === 'dates' || typeof keyCol !== 'number') return null

      const data = value[0] === null && value[1] === null ? [] : [...value]

      return createDataset({
        label: capitalize(name),
        hex: keyCode[keyCol],
        data,
        isHidden: chartType !== 'BOTH' && !key.includes(chartType),
        isDesktop,
      })
    })
    .filter(Boolean)

  const { min, max, ticks, step } = getYAxisBounds(
    datasets.filter(ds => !ds?.hidden).map(ds => ds?.data) as unknown as (string | null)[]
  )

  return {
    lineChartData: {
      labels: dates,
      datasets,
    },
    chartDataOptions: getHistoryLineChartOptions({
      yMin: min,
      yMax: max,
      yTicksLimit: ticks,
      yStepSize: step,
      xTicksRotation: isDesktop ? 0 : 45,
    }),
  }
}
interface Props {
  title?: string
  containerStyle?: any
  labels: LineChartLabel[]
  playerIds: string[]
}

const HistoryLineChart = ({ playerIds, title, labels = defaultLabels, containerStyle }: Props) => {
  const dispatch = useDispatch()
  const data = useSelector((state: AppState) => state.ratings.ratings)
  const loadingData = useSelector((state: AppState) => state.ratings.loadingRatings)
  const errorData = useSelector((state: AppState) => state.ratings.errorRatings)
  const earliestRecord = useSelector((state: AppState) => state.ratings.earliestRecord)
  const dateRange = useSelector((state: AppState) => state.ratings.wtnChartRange)
  const [gameType, setGameType] = useState<GameType>(MatchUpTypeEnum.SINGLES)
  const windowWidth = useWindowSize().width
  const isDesktop = !!windowWidth && windowWidth >= TABLET_BREAKPOINT
  const { minPickerDate, maxPickerDate } = useMemo(
    () => ({
      minPickerDate: earliestRecord ? new Date(earliestRecord) : undefined,
      maxPickerDate: new Date(),
    }),
    [earliestRecord]
  )

  const selectedRange: DateRange = useMemo(() => {
    const start = dateRange.start || earliestRecord
    const end = dateRange.end
    const dateStart = start ? new Date(start) : undefined
    const dateEnd = end ? new Date(end) : new Date()

    return { start: dateStart, end: dateEnd }
  }, [dateRange, earliestRecord])

  const setDateRange = useCallback(
    (dateRange: { start: string; end: string }) => dispatch(setWtnChartRange(dateRange)),
    [dispatch]
  )

  const dateRangeConfigMobile = useMemo(() => {
    const { start, end } = selectedRange

    if (!start || !end || !minPickerDate || !maxPickerDate) return undefined
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      minDate: minPickerDate.toISOString(),
      maxDate: maxPickerDate.toISOString(),
      timeRangeDispatch: setDateRange,
    }
  }, [selectedRange, setDateRange, minPickerDate, maxPickerDate])

  const { ratings, dates } = useMemo(() => {
    if (labels.length / 2 === data.length) {
      return formatData({
        labels,
        ratingsData: data,
        startDate: dateRange.start,
        endDate: dateRange.end,
      })
    } else {
      return { ratings: {}, dates: [] }
    }
  }, [labels, data, dateRange])

  const { lineChartData, chartDataOptions } = useMemo(
    () => chartData({ data: ratings, chartType: gameType, dates, labels, isDesktop }),
    [ratings, dates, gameType, labels, isDesktop]
  )

  const fetchRatingsHistoryMemo = useCallback(
    (sd: string, ed: string) =>
      dispatch(
        fetchRatingsHistory(
          playerIds.filter(id => id),
          sd,
          ed
        )
      ),
    [dispatch, playerIds]
  )

  const fetchEarliestRecordMemo = useCallback(
    () => dispatch(fetchEarliestRecord(playerIds.filter(id => id))),
    [dispatch, playerIds]
  )

  const handleApply = useCallback(
    (range: DateRange) => {
      dispatch(
        setDateRange({
          start: range?.start?.toISOString() as string,
          end: range?.end?.toISOString() as string,
        })
      )
    },
    [dispatch, setDateRange]
  )

  useEffect(() => {
    fetchEarliestRecordMemo()
  }, [fetchEarliestRecordMemo])

  useEffect(() => {
    fetchRatingsHistoryMemo(dateRange.start, dateRange.end)
  }, [fetchRatingsHistoryMemo, dateRange])

  const isDatasetEmpty = checkDatasetEmpty(lineChartData.datasets)
  const hasNoData = loadingData || errorData || isDatasetEmpty
  return (
    <>
      {title && <h2 className={styles.header}>{title}</h2>}
      {isDesktop ? (
        <>
          {earliestRecord && (
            <DateRangePickerWTN
              containerClassName={styles.datePickerWrapper}
              minDate={minPickerDate}
              maxDate={maxPickerDate}
              selectedRange={selectedRange}
              setSelectedRange={handleApply}
            />
          )}
          <FilterDropdown
            options={matchTypes}
            selectedKey={gameType}
            onSelect={({ key }) => setGameType(key)}
            style={{ marginLeft: '20px' }}
          />
        </>
      ) : (
        <ChartMobileFilter
          gameTypeFilterData={{
            gameTypeItems: matchTypes,
            selectedGameTypeItem: gameType,
            gameTypeDispatch: setGameType,
          }}
          timeRangeFilterData={dateRangeConfigMobile}
          classNames={{ filterBar: styles.filterBar }}
        />
      )}

      <div className={containerStyle || ''}>
        {hasNoData ? (
          <NoDataContainer>
            {loadingData ? (
              <LoadingSpinnerWithContainer containerHeight={350} />
            ) : (
              <p className={cx(styles.noDataText, 'history-line-chart_noDataText--override')}>
                {errorData ? i18n.t('strCharts.errorFetchingData') : i18n.t('strCharts.noData')}
              </p>
            )}
          </NoDataContainer>
        ) : (
          <div className={styles.chart}>
            <LineChart data={lineChartData} options={chartDataOptions} />
          </div>
        )}
      </div>
    </>
  )
}

export default HistoryLineChart
